/**
 * Board camera.
 *
 * Works in "world" units where one logical cell is {@link CELL_SIZE} units. The camera owns
 * fit-to-board, zoom limits, bounded panning and centring, and converts between screen and
 * board coordinates for hit testing. It holds no PixiJS types so it can be unit tested.
 */

export const CELL_SIZE = 64;

export interface CameraLimits {
  readonly minScale: number;
  readonly maxScale: number;
  /** Fraction of the viewport left as breathing room when fitting. */
  readonly fitPadding: number;
}

export const DEFAULT_LIMITS: CameraLimits = {
  minScale: 0.12,
  maxScale: 3.2,
  fitPadding: 0.94,
};

export interface Point {
  readonly x: number;
  readonly y: number;
}

export class BoardCamera {
  private viewportWidth = 1;
  private viewportHeight = 1;
  private columns = 1;
  private rows = 1;
  private scaleValue = 1;
  private offsetX = 0;
  private offsetY = 0;
  private fitScaleValue = 1;

  /**
   * The part of the grid worth looking at, in cells. A silhouette rarely fills its bounding
   * grid, and fitting the whole grid would waste most of a phone screen on empty margin.
   */
  private contentMinX = 0;
  private contentMinY = 0;
  private contentColumns = 1;
  private contentRows = 1;

  constructor(private readonly limits: CameraLimits = DEFAULT_LIMITS) {}

  get scale(): number {
    return this.scaleValue;
  }

  get x(): number {
    return this.offsetX;
  }

  get y(): number {
    return this.offsetY;
  }

  get fitScale(): number {
    return this.fitScaleValue;
  }

  get minScale(): number {
    // Never allow zooming out further than the fitted view; there is nothing to see there.
    return Math.min(this.fitScaleValue, this.limits.maxScale);
  }

  get maxScale(): number {
    return this.limits.maxScale;
  }

  get boardWidth(): number {
    return this.columns * CELL_SIZE;
  }

  get boardHeight(): number {
    return this.rows * CELL_SIZE;
  }

  /** Width of the region the camera actually frames. */
  get contentWidth(): number {
    return this.contentColumns * CELL_SIZE;
  }

  get contentHeight(): number {
    return this.contentRows * CELL_SIZE;
  }

  private get contentLeft(): number {
    return this.contentMinX * CELL_SIZE;
  }

  private get contentTop(): number {
    return this.contentMinY * CELL_SIZE;
  }

  setViewport(width: number, height: number): void {
    this.viewportWidth = Math.max(1, width);
    this.viewportHeight = Math.max(1, height);
    this.recomputeFitScale();
    this.clamp();
  }

  setBoardSize(columns: number, rows: number): void {
    this.columns = Math.max(1, columns);
    this.rows = Math.max(1, rows);
    this.setContentBounds(0, 0, this.columns, this.rows);
  }

  /** Frames a sub-rectangle of the grid, given in cells. */
  setContentBounds(minX: number, minY: number, columns: number, rows: number): void {
    this.contentMinX = Math.max(0, Math.min(minX, this.columns - 1));
    this.contentMinY = Math.max(0, Math.min(minY, this.rows - 1));
    this.contentColumns = Math.max(1, Math.min(columns, this.columns - this.contentMinX));
    this.contentRows = Math.max(1, Math.min(rows, this.rows - this.contentMinY));
    this.recomputeFitScale();
  }

  private recomputeFitScale(): void {
    const scaleX = (this.viewportWidth * this.limits.fitPadding) / this.contentWidth;
    const scaleY = (this.viewportHeight * this.limits.fitPadding) / this.contentHeight;
    const fit = Math.min(scaleX, scaleY);
    this.fitScaleValue = Math.min(Math.max(fit, this.limits.minScale), this.limits.maxScale);
  }

  /** Fits the whole board into the viewport and centres it. */
  fit(): void {
    this.recomputeFitScale();
    this.scaleValue = this.fitScaleValue;
    this.centre();
  }

  private centre(): void {
    this.offsetX =
      (this.viewportWidth - this.contentWidth * this.scaleValue) / 2 -
      this.contentLeft * this.scaleValue;
    this.offsetY =
      (this.viewportHeight - this.contentHeight * this.scaleValue) / 2 -
      this.contentTop * this.scaleValue;
  }

  panBy(dx: number, dy: number): void {
    this.offsetX += dx;
    this.offsetY += dy;
    this.clamp();
  }

  /** Zooms around a screen anchor so the point under the finger stays put. */
  zoomTo(nextScale: number, anchorX: number, anchorY: number): void {
    const clamped = Math.min(Math.max(nextScale, this.minScale), this.maxScale);
    if (clamped === this.scaleValue) return;
    const worldX = (anchorX - this.offsetX) / this.scaleValue;
    const worldY = (anchorY - this.offsetY) / this.scaleValue;
    this.scaleValue = clamped;
    this.offsetX = anchorX - worldX * clamped;
    this.offsetY = anchorY - worldY * clamped;
    this.clamp();
  }

  zoomBy(factor: number, anchorX: number, anchorY: number): void {
    this.zoomTo(this.scaleValue * factor, anchorX, anchorY);
  }

  /** Keeps the framed content within the viewport, centring on any axis where it fits. */
  clamp(): void {
    const scaledWidth = this.contentWidth * this.scaleValue;
    const scaledHeight = this.contentHeight * this.scaleValue;
    const left = this.contentLeft * this.scaleValue;
    const top = this.contentTop * this.scaleValue;

    if (scaledWidth <= this.viewportWidth) {
      this.offsetX = (this.viewportWidth - scaledWidth) / 2 - left;
    } else {
      const minX = this.viewportWidth - scaledWidth - left;
      this.offsetX = Math.min(-left, Math.max(minX, this.offsetX));
    }

    if (scaledHeight <= this.viewportHeight) {
      this.offsetY = (this.viewportHeight - scaledHeight) / 2 - top;
    } else {
      const minY = this.viewportHeight - scaledHeight - top;
      this.offsetY = Math.min(-top, Math.max(minY, this.offsetY));
    }
  }

  screenToWorld(screenX: number, screenY: number): Point {
    return {
      x: (screenX - this.offsetX) / this.scaleValue,
      y: (screenY - this.offsetY) / this.scaleValue,
    };
  }

  worldToScreen(worldX: number, worldY: number): Point {
    return {
      x: worldX * this.scaleValue + this.offsetX,
      y: worldY * this.scaleValue + this.offsetY,
    };
  }

  /** Board cell under a screen point, or null when outside the grid. */
  screenToCell(screenX: number, screenY: number): { column: number; row: number } | null {
    const world = this.screenToWorld(screenX, screenY);
    const column = Math.floor(world.x / CELL_SIZE);
    const row = Math.floor(world.y / CELL_SIZE);
    if (column < 0 || row < 0 || column >= this.columns || row >= this.rows) return null;
    return { column, row };
  }

  /** Effective on-screen size of one cell; used to check touch-target adequacy. */
  get cellScreenSize(): number {
    return CELL_SIZE * this.scaleValue;
  }
}
